import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'
import api from '../../api/axios'
import { API_ENDPOINTS } from '../../api/endpoints'
import axios from 'axios'
import i18n from '../../i18n'
import { getApiErrorMessage } from '../../utils/apiError'

/** Error response shape from the serviceman auth backend. */
interface ApiErrorResponse {
  error: string
  message?: string
  remaining_attempts?: number
  locked?: boolean
}

/** Successful PIN validation response containing the JWT token. */
interface PinValidationResponse {
  success: boolean
  token: string
  user?: {
    email: string
    name?: string
  }
  remaining_attempts?: number
}

/**
 * Redux state for the authentication slice.
 *
 * Manages the PIN-based authentication flow: email entry → PIN request → PIN validation → JWT token.
 *
 * @remarks
 * Backend auth flow:
 * 1. `POST /serviceman/auth/request-pin` — generates 6-digit PIN (15min expiry, max 3 attempts, rate limit 10/hour/email)
 * 2. `POST /serviceman/auth/validate-pin` — validates PIN, returns JWT with claims `{email, target_env, session, exp, iat}`
 * 3. JWT session written to Redis for all user environments with 7-day TTL.
 * Backend returns generic success on PIN request to prevent email enumeration.
 *
 * Token is persisted in `localStorage` under `'token'` and `'email'` keys.
 * PIN auth is force-enabled (`pinAuthEnabled: true`); magic link is a legacy fallback.
 */
interface AuthState {
  token: string | null
  email: string
  pin: string
  isAuthenticated: boolean
  pinAuthEnabled: boolean // Feature flag
  pinSent: boolean // PIN has been sent to email
  loading: boolean
  error: string | null
  emailError: string | null
  pinError: string | null
  remainingAttempts: number // Remaining PIN validation attempts (security)
}

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  email: localStorage.getItem('email') || '',
  pin: '',
  isAuthenticated: !!localStorage.getItem('token'),
  pinAuthEnabled: true, // Force enabled by default
  pinSent: false,
  loading: false,
  error: null,
  emailError: null,
  pinError: null,
  remainingAttempts: 3,
}

// Async Thunks

/**
 * Fetches feature flags for auth configuration.
 *
 * Currently hardcoded to return `{ pinAuthEnabled: true }` since PIN auth
 * is the only supported authentication method.
 *
 * @returns `{ pinAuthEnabled: true }`
 */
export const fetchFeatureFlags = createAsyncThunk('auth/fetchFeatureFlags', async () => {
  // Force PIN auth enabled
  return { pinAuthEnabled: true }
})

/**
 * Requests a 6-digit PIN code to be sent to the user's email.
 *
 * The email is normalized (trimmed, lowercased) before sending.
 * On success, sets `pinSent: true` to transition the UI to PIN entry.
 *
 * @remarks
 * Backend: `POST /serviceman/auth/request-pin` with `{ email }` body.
 * PIN expires after 15 minutes. Max 3 validation attempts per PIN.
 * Rate limited to 10 requests/hour/email.
 * Backend always returns success to prevent email enumeration attacks.
 *
 * @param email - The user's email address
 * @returns `{ email }` (normalized) on success
 * @throws Rejects with Finnish error message on network/server failure
 */
export const requestPIN = createAsyncThunk('auth/requestPIN', async (email: string, { rejectWithValue }) => {
  //const { t } = useTranslation()
  try {
    const response = await api.post(API_ENDPOINTS.AUTH_REQUEST_PIN, {
      email: email.trim().toLowerCase(),
    })

    if (response.data.success) {
      return { email: email.trim().toLowerCase() }
    }

    // If the backend returns success: false, treat it as a failure
    if (!response.data.success) {
      return rejectWithValue(i18n.t('auth.pinSendFailed'))
    }

    return rejectWithValue('PIN request failed')
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error, 'auth.pinSendFailed'))
  }
})

/**
 * Validates the user-entered PIN and obtains a JWT token on success.
 *
 * Stores the JWT in `localStorage` and sets `isAuthenticated: true`.
 * Handles specific error cases with localized Finnish messages:
 * - Expired PIN → "Kirjautumiskoodi on vanhentunut"
 * - Locked account → "Koodi on lukittu"
 * - Wrong PIN with remaining attempts → "Virheellinen koodi. Yrityksiä jäljellä: N"
 *
 * @remarks
 * Backend: `POST /serviceman/auth/validate-pin` with `{ email, pin }` body.
 * Response: `{ success: true, token: string }` or error with `remaining_attempts`.
 * JWT claims: `{ email, target_env (comma-separated), session, iat, exp }`.
 *
 * @param params - Object with `email` and `pin` strings
 * @returns `{ token, email }` on success
 * @throws Rejects with `{ message: string, remainingAttempts?: number }` on validation failure
 */
export const validatePIN = createAsyncThunk(
  'auth/validatePIN',
  async ({ email, pin }: { email: string; pin: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<PinValidationResponse>(API_ENDPOINTS.AUTH_VALIDATE_PIN, {
        email: email.trim().toLowerCase(),
        pin: pin.trim(),
      })

      if (response.data.success) {
        const token = response.data.token

        // Store token in localStorage
        localStorage.setItem('token', token)
        localStorage.setItem('email', email)

        return {
          token,
          email,
        }
      }

      return rejectWithValue('Validation failed')
    } catch (error) {
      let errorMessage = getApiErrorMessage(error, 'auth.pinError')
      let remainingAttempts: number | undefined

      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as ApiErrorResponse | undefined
        if (errorData) {
          // The auth backend phrases these in English, so match the raw value before translating.
          const backendError = errorData.error ?? ''
          remainingAttempts = errorData.remaining_attempts

          if (backendError.includes('expired') || backendError.includes('vanhentunut')) {
            errorMessage = i18n.t('auth.pinExpired')
          } else if (backendError.includes('locked') || backendError.includes('lukittu')) {
            errorMessage = i18n.t('auth.pinLocked')
          } else if (remainingAttempts === 0) {
            errorMessage = i18n.t('auth.pinLockedNoAttempts')
          } else if (remainingAttempts !== undefined && remainingAttempts > 0) {
            errorMessage = `${i18n.t('auth.pinError')}. ${i18n.t('auth.attemptsRemaining', { count: remainingAttempts })}`
          }
        }
      }

      return rejectWithValue({ message: errorMessage, remainingAttempts })
    }
  }
)

/**
 * Verifies an existing token (from localStorage or magic link URL).
 *
 * @remarks
 * Backend: `GET /serviceman/auth/verify` with Bearer token header.
 * Used on app load to check if the stored token is still valid.
 *
 * @param token - The JWT token string to verify
 * @returns `{ token }` if valid
 * @throws Rejects with error message if token is invalid or expired
 */
export const verifyToken = createAsyncThunk('auth/verifyToken', async (token: string, { rejectWithValue }) => {
  try {
    const response = await api.get(API_ENDPOINTS.AUTH_VERIFY)

    if (response.data.valid) {
      localStorage.setItem('token', token)
      return { token }
    }

    return rejectWithValue('Invalid token')
  } catch (error) {
    return rejectWithValue('Token verification failed')
  }
})

/**
 * Sends a magic link email as fallback authentication.
 *
 * @deprecated PIN auth is the primary method. Magic link is retained as legacy fallback.
 *
 * @remarks
 * Backend: `POST /serviceman/auth/link-query` with `{ email, invitation: false, newServiceman: false }`.
 *
 * @param email - The user's email address
 * @returns `{ email }` on success
 * @throws Rejects with Finnish error message if email not found or send fails
 */
export const useMagicLinkFallback = createAsyncThunk('auth/magicLink', async (email: string, { rejectWithValue }) => {
  try {
    const response = await api.post(API_ENDPOINTS.AUTH_LINK_QUERY, {
      email: email.trim().toLowerCase(),
      invitation: false,
      newServiceman: false,
    })

    if (response.data.success) {
      return { email: email.trim().toLowerCase() }
    }

    return rejectWithValue('Sähköpostiosoitetta ei löytynyt. Tarkista oikeinkirjoitus.')
  } catch (error) {
    return rejectWithValue('Turvalinkin lähetys epäonnistui.')
  }
})

/**
 * Logs out the user by calling the backend logout endpoint and clearing local storage.
 *
 * @remarks
 * Backend: `POST /serviceman/auth/logout`.
 * Always clears `localStorage` token and email, even if the API call fails.
 *
 * @returns Empty object `{}`
 */
export const logoutUser = createAsyncThunk('auth/logout', async () => {
  try {
    await api.post(API_ENDPOINTS.AUTH_LOGOUT)
    localStorage.removeItem('token')
    localStorage.removeItem('email')
    localStorage.removeItem('last_env')
    return {}
  } catch (error) {
    localStorage.removeItem('token')
    localStorage.removeItem('email')
    localStorage.removeItem('last_env')
    return {}
  }
})

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = true
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload
    },
    setPIN: (state, action: PayloadAction<string>) => {
      state.pin = action.payload
    },
    clearError: state => {
      state.error = null
      state.emailError = null
      state.pinError = null
    },
    resetPINState: state => {
      state.pin = ''
      state.pinSent = false
      state.pinError = null
      state.remainingAttempts = 3
    },
  },
  extraReducers: builder => {
    // Fetch feature flags
    builder.addCase(fetchFeatureFlags.fulfilled, (state, action) => {
      state.pinAuthEnabled = action.payload.pinAuthEnabled
    })

    // Request PIN
    builder
      .addCase(requestPIN.pending, state => {
        state.loading = true
        state.emailError = null
      })
      .addCase(requestPIN.fulfilled, (state, action) => {
        state.loading = false
        state.email = action.payload.email
        state.pinSent = true
        state.emailError = null
      })
      .addCase(requestPIN.rejected, (state, action) => {
        state.loading = false
        state.emailError = action.payload as string
      })

    // Validate PIN
    builder
      .addCase(validatePIN.pending, state => {
        state.loading = true
        state.pinError = null
      })
      .addCase(validatePIN.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload.token
        state.email = action.payload.email
        state.isAuthenticated = true
        state.pinError = null
        state.pinSent = false
        state.pin = ''
      })
      .addCase(validatePIN.rejected, (state, action) => {
        state.loading = false
        const payload = action.payload as { message: string; remainingAttempts?: number } | undefined
        if (payload) {
          state.pinError = payload.message
          if (payload.remainingAttempts !== undefined) {
            state.remainingAttempts = payload.remainingAttempts
          }
        } else {
          state.pinError = 'Virheellinen PIN-koodi'
        }
      })

    // Verify token
    builder
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.token = action.payload.token
        state.isAuthenticated = true
      })
      .addCase(verifyToken.rejected, state => {
        state.token = null
        state.isAuthenticated = false
      })

    // Magic link fallback
    builder
      .addCase(useMagicLinkFallback.pending, state => {
        state.loading = true
        state.emailError = null
      })
      .addCase(useMagicLinkFallback.fulfilled, state => {
        state.loading = false
        state.emailError = null
      })
      .addCase(useMagicLinkFallback.rejected, (state, action) => {
        state.loading = false
        state.emailError = action.payload as string
      })

    // Logout
    builder.addCase(logoutUser.fulfilled, state => {
      state.token = null
      state.email = ''
      state.isAuthenticated = false
      state.pinSent = false
      state.pin = ''
    })
  },
})

// Actions
export const { setToken, setEmail, setPIN, clearError, resetPINState } = authSlice.actions

// Selectors
/** Selects the entire auth state. */
export const selectAuth = (state: RootState) => state.auth
/** Whether the user has a valid JWT token. */
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated
/** The current JWT token string, or null. */
export const selectAuthToken = (state: RootState) => state.auth.token
/** General auth error message (Finnish). */
export const selectAuthError = (state: RootState) => state.auth.error
/** Error from the email/PIN-request step. */
export const selectEmailError = (state: RootState) => state.auth.emailError
/** Error from the PIN validation step, with attempt context. */
export const selectPinError = (state: RootState) => state.auth.pinError
/** Whether PIN-based auth is enabled (currently always true). */
export const selectPinAuthEnabled = (state: RootState) => state.auth.pinAuthEnabled
/** Whether a PIN has been sent and the UI should show the PIN entry form. */
export const selectPinSent = (state: RootState) => state.auth.pinSent
/** Remaining PIN validation attempts before lockout (max 3). */
export const selectRemainingAttempts = (state: RootState) => state.auth.remainingAttempts
/** Whether an auth operation is in progress. */
export const selectAuthLoading = (state: RootState) => state.auth.loading

// Reducer
export default authSlice.reducer
